import type { OverviewBlock, OverviewBlockWidth } from '../api/types';
import { GRID_COLUMNS, blockColumns } from '../constants';

export type BlockErrors = { criteria?: string };

export const NO_CRITERIA_ERROR = 'Kies minstens één criterium.';
export const UNKNOWN_CRITERIA_ERROR = 'Dit criterium bestaat niet meer. Verwijder het.';

/** Nieuw blok onderaan: nog geen criteria, kwartbreedte. */
export function emptyBlock(): OverviewBlock {
  return { criteria: [], width: 'quarter' };
}

/**
 * Verplaatst het item op `from` naar `to` (slepen én pijltjes). Geeft altijd een nieuwe
 * array; dezelfde plek of een index buiten de lijst laat de volgorde ongewijzigd.
 */
export function moveBlock<T>(items: readonly T[], from: number, to: number): T[] {
  const next = [...items];
  const inRange = (i: number) => i >= 0 && i < next.length;
  if (from === to || !inRange(from) || !inRange(to)) return next;
  next.splice(to, 0, ...next.splice(from, 1));
  return next;
}

/** De nieuwe index van het geselecteerde blok nadat `from` naar `to` is verplaatst. */
export function followIndex(selected: number, from: number, to: number): number {
  if (selected === from) return to;
  if (from < selected && to >= selected) return selected - 1;
  if (from > selected && to <= selected) return selected + 1;
  return selected;
}

/** Fout bij het veld "Criteria in dit blok"; leeg object = geen fout. */
export function blockErrors(block: OverviewBlock, knownCodes: ReadonlySet<string>): BlockErrors {
  if (block.criteria.length === 0) return { criteria: NO_CRITERIA_ERROR };
  if (block.criteria.some((code) => !knownCodes.has(code))) {
    return { criteria: UNKNOWN_CRITERIA_ERROR };
  }
  return {};
}

export function hasErrors(blocks: readonly OverviewBlock[], knownCodes: ReadonlySet<string>): boolean {
  return blocks.some((block) => blockErrors(block, knownCodes).criteria !== undefined);
}

/** Zelfde regel als resolveBlock.ts: titel, anders de criterianamen met " + ". */
export function blockLabel(block: OverviewBlock, nameByCode: ReadonlyMap<string, string>): string {
  return (
    block.title?.trim() ||
    block.criteria.map((code) => nameByCode.get(code) ?? code).join(' + ') ||
    'Nieuw blok'
  );
}

/** Labels van de blokken waarin `code` staat ("Staat ook in: …"). */
export function blocksContaining(
  code: string,
  blocks: readonly OverviewBlock[],
  nameByCode: ReadonlyMap<string, string>,
): string[] {
  return blocks
    .filter((block) => block.criteria.includes(code))
    .map((block) => blockLabel(block, nameByCode));
}

/** Zoals het naar de backend gaat: titel getrimd, een lege titel weggelaten. */
export function toSavedBlocks(blocks: readonly OverviewBlock[]): OverviewBlock[] {
  return blocks.map(({ criteria, title, width }) => {
    const trimmed = title?.trim();
    return trimmed ? { criteria, title: trimmed, width } : { criteria, width };
  });
}

export type BlockRow = {
  /** Posities in de blokkenlijst die op deze rij staan. */
  indexes: number[];
  /** Bezette kolommen van GRID_COLUMNS. */
  used: number;
};

/**
 * Rijindeling op desktop, zoals CSS Grid zonder dense-plaatsing: blokken staan in config-volgorde
 * van links naar rechts; past een blok niet meer in de rij, dan begint een nieuwe rij en wordt
 * de rest van de vorige rij niet meer aangevuld.
 */
export function blockRows(blocks: readonly { width: OverviewBlockWidth }[]): BlockRow[] {
  const rows: BlockRow[] = [];
  blocks.forEach((block, index) => {
    const span = blockColumns[block.width];
    const last = rows[rows.length - 1];
    if (last && last.used + span <= GRID_COLUMNS) {
      last.indexes.push(index);
      last.used += span;
    } else {
      rows.push({ indexes: [index], used: span });
    }
  });
  return rows;
}

export type LayoutSection = {
  /**
   * 'row': een rij met een kwartblok; elke kaart houdt zijn eigen hoogte.
   * 'flow': aaneengesloten rijen zonder kwartblok; blokken mogen omhoog schuiven onder een
   * kortere buur, zodat er geen ruimte tussen halve blokken blijft.
   */
  kind: 'row' | 'flow';
  rows: BlockRow[];
};

/** Deelt de blokken in secties in (zie LayoutSection), in config-volgorde. */
export function layoutSections(blocks: readonly { width: OverviewBlockWidth }[]): LayoutSection[] {
  const sections: LayoutSection[] = [];
  for (const row of blockRows(blocks)) {
    const hasQuarter = row.indexes.some((i) => blocks[i]!.width === 'quarter');
    const last = sections[sections.length - 1];
    if (!hasQuarter && last?.kind === 'flow') last.rows.push(row);
    else sections.push({ kind: hasQuarter ? 'row' : 'flow', rows: [row] });
  }
  return sections;
}
